"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Shield,
  Lock,
  KeyRound,
  FileCheck2,
  Database,
  Globe,
  Server,
  Eye,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  ShieldCheck,
  Download,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { ShimmerBar } from "@/components/site/StatePrimitives";
import {
  seoMeta,
  seoLinks,
  jsonLd,
  breadcrumbLd,
  organizationLd,
  itemListLd,
  SITE,
} from "@/lib/seo/seo";

const SECURITY_PATH = "/security";
const SECURITY_TITLE = "امنیت و حریم خصوصی | ترندی اسمارت";
const SECURITY_DESC =
  "زیرساخت امن ترندی اسمارت: رمزنگاری end-to-end، ایزولاسیون داده، کنترل دسترسی نقش‌محور، گواهی‌های ISO 27001، SOC 2 و انطباق با GDPR.";
const SECURITY_KEYWORDS = [
  "امنیت ترندی اسمارت",
  "Trendi Smart security",
  "ISO 27001",
  "SOC 2",
  "GDPR",
  "رمزنگاری",
  "حریم خصوصی",
  "data protection",
];

const COMPLIANCE_ITEMS = [
  { name: "ISO/IEC 27001", description: "سیستم مدیریت امنیت اطلاعات" },
  { name: "SOC 2 Type II", description: "کنترل‌های امنیتی، در دسترس بودن و محرمانگی" },
  { name: "GDPR", description: "انطباق با مقررات حریم خصوصی اتحادیه اروپا" },
  { name: "PCI DSS SAQ-A", description: "پردازش امن پرداخت‌های کارتی" },
  { name: "HIPAA-Ready", description: "آمادگی برای داده‌های حساس درمانی" },
];

type Scenario =
  "loaded" | "loading" | "incident-active" | "audit-mode" | "minimal" | "compliant-only";

export default function SecurityPage() {
  const [scenario, setScenario] = useState<Scenario>("loading");

  useEffect(() => {
    if (scenario !== "loading") return;
    const t = setTimeout(() => setScenario("loaded"), 1100);
    return () => clearTimeout(t);
  }, [scenario]);

  return (
    <SiteShell>
      {scenario === "incident-active" && <IncidentBanner />}
      {scenario === "audit-mode" && <AuditBanner />}

      <Hero />

      {scenario === "loading" ? (
        <SkelGrid />
      ) : (
        <>
          <Pillars compact={scenario === "minimal"} />
          {scenario !== "minimal" && <DataLayer />}
          <Compliance only={scenario === "compliant-only"} />
          {scenario !== "minimal" && <Practices />}
          <Trust />
        </>
      )}
      <CTA />
    </SiteShell>
  );
}

function IncidentBanner() {
  return (
    <div className="border-b border-error/40 bg-error/10">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-3 sm:px-6">
        <AlertTriangle size={16} className="shrink-0 text-error" />
        <div className="flex-1 text-[12.5px] font-bold text-foreground">
          رویداد امنیتی فعال — تیم ما در حال بررسی است.
          <Link href="/status" className="mr-2 text-error underline">
            جزئیات در صفحه وضعیت
          </Link>
        </div>
      </div>
    </div>
  );
}

function AuditBanner() {
  return (
    <div className="border-b border-secondary-main/40 bg-secondary-main/10">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-5 py-3 sm:px-6">
        <FileCheck2 size={16} className="shrink-0 text-secondary-main" />
        <div className="flex-1 text-[12.5px] font-bold text-foreground">
          گزارش ممیزی Q1 ۱۴۰۴ منتشر شد.
        </div>
        <button className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-main px-3 py-1.5 text-[11.5px] font-black text-white">
          <Download size={12} /> دانلود PDF
        </button>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-6 sm:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <ShieldCheck size={14} className="text-primary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">امنیت در DNA محصول</span>
        </div>
        <h1 className="mx-auto mb-5 max-w-3xl text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
          داده‌های شما،
          <span className="block brand-gradient-text">دژ امن ما</span>
        </h1>
        <p className="mx-auto max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
          از رمزنگاری end-to-end تا کنترل دسترسی نقش‌محور و ممیزی مستمر — ترندی اسمارت با بالاترین
          استانداردهای جهانی ساخته شده.
        </p>
      </motion.div>
    </section>
  );
}

const PILLARS = [
  {
    icon: Lock,
    title: "رمزنگاری end-to-end",
    desc: "تمام داده‌ها در حال انتقال (TLS 1.3) و در حال ذخیره‌سازی (AES-256) رمزنگاری می‌شوند.",
    color: "from-primary-main to-primary-300",
  },
  {
    icon: KeyRound,
    title: "کنترل دسترسی پیشرفته",
    desc: "RBAC، 2FA اجباری برای ادمین‌ها و SSO سازمانی برای پلن Enterprise.",
    color: "from-secondary-main to-secondary-300",
  },
  {
    icon: Eye,
    title: "Audit Log کامل",
    desc: "هر تغییر، ورود و عملیات حساس برای ۹۰ روز قابل بازبینی است.",
    color: "from-purple to-blue",
  },
  {
    icon: Server,
    title: "زیرساخت ایزوله",
    desc: "محیط‌های production و staging کاملاً جدا، پشتیبان‌گیری روزانه.",
    color: "from-teal to-light-blue",
  },
  {
    icon: Shield,
    title: "حفاظت در برابر حمله",
    desc: "WAF، rate-limiting و محافظت DDoS لایه ۷ به‌صورت پیش‌فرض.",
    color: "from-pink to-secondary-main",
  },
  {
    icon: Database,
    title: "حاکمیت داده",
    desc: "حق فراموش‌شدن، خروجی داده، و حذف کامل با درخواست شما.",
    color: "from-blue to-purple",
  },
];

function Pillars({ compact }: { compact?: boolean }) {
  const list = compact ? PILLARS.slice(0, 3) : PILLARS;
  return (
    <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
      <h2 className="mb-6 text-center text-2xl font-black tracking-tight text-foreground sm:text-3xl">
        لایه‌های امنیتی
      </h2>
      <div className={`grid gap-4 sm:grid-cols-2 ${compact ? "lg:grid-cols-3" : "lg:grid-cols-3"}`}>
        {list.map((p, i) => {
          const I = p.icon;
          return (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary-main/40"
            >
              <div
                className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${p.color} text-white shadow-md`}
              >
                <I size={18} />
              </div>
              <h3 className="mb-2 text-[15px] font-black text-foreground">{p.title}</h3>
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">{p.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function DataLayer() {
  const items = [
    { l: "محل ذخیره‌سازی", v: "ایران + EU" },
    { l: "پشتیبان‌گیری", v: "هر ۲۴ ساعت" },
    { l: "RTO / RPO", v: "۴ ساعت / ۱ ساعت" },
    { l: "زمان حذف نهایی", v: "۳۰ روز" },
    { l: "نگهداری Log", v: "۹۰ روز" },
    { l: "رمزنگاری دیسک", v: "AES-256" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <div className="mb-5 flex items-center gap-3">
          <Database size={18} className="text-primary-main" />
          <h2 className="text-[18px] font-black text-foreground">داده شما در یک نگاه</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div
              key={it.l}
              className="flex items-center justify-between rounded-xl border border-border bg-background p-3.5"
            >
              <span className="text-[12px] text-muted-foreground">{it.l}</span>
              <span className="text-[12.5px] font-black text-foreground">{it.v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COMPLIANCE = [
  { name: "ISO 27001", status: "active", desc: "مدیریت امنیت اطلاعات" },
  { name: "SOC 2 Type II", status: "in-progress", desc: "ممیزی کنترل‌های امنیتی" },
  { name: "GDPR", status: "active", desc: "حریم خصوصی اروپا" },
  { name: "PCI-DSS", status: "active", desc: "امنیت پرداخت" },
  { name: "HIPAA", status: "planned", desc: "بهداشت و درمان" },
  { name: "قانون داده ایران", status: "active", desc: "انطباق محلی" },
];

function Compliance({ only }: { only?: boolean }) {
  const list = only ? COMPLIANCE.filter((c) => c.status === "active") : COMPLIANCE;
  return (
    <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
      <h2 className="mb-6 text-center text-2xl font-black tracking-tight text-foreground sm:text-3xl">
        گواهی‌ها و انطباق
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c, i) => {
          const tone =
            c.status === "active"
              ? {
                  bg: "bg-success/15",
                  text: "text-success",
                  label: "فعال",
                  icon: <CheckCircle2 size={11} />,
                }
              : c.status === "in-progress"
                ? {
                    bg: "bg-secondary-main/15",
                    text: "text-secondary-main",
                    label: "در حال ممیزی",
                    icon: <FileCheck2 size={11} />,
                  }
                : {
                    bg: "bg-muted",
                    text: "text-muted-foreground",
                    label: "برنامه‌ریزی‌شده",
                    icon: <FileCheck2 size={11} />,
                  };
          return (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center justify-between rounded-2xl border border-border bg-card p-5"
            >
              <div className="min-w-0">
                <div className="text-[14px] font-black text-foreground">{c.name}</div>
                <div className="mt-0.5 text-[11.5px] text-muted-foreground">{c.desc}</div>
              </div>
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black ${tone.bg} ${tone.text}`}
              >
                {tone.icon} {tone.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Practices() {
  const items = [
    { icon: Globe, title: "تست نفوذ سالانه", desc: "توسط شرکت‌های مستقل امنیتی." },
    { icon: KeyRound, title: "Bug Bounty", desc: "برنامه پاداش به محققان امنیتی." },
    { icon: Eye, title: "مانیتورینگ ۲۴/۷", desc: "تشخیص ناهنجاری مبتنی بر AI." },
    { icon: Lock, title: "Secret Rotation خودکار", desc: "چرخش کلیدها هر ۳۰ روز." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
      <div className="rounded-3xl border border-border bg-gradient-to-br from-card to-background p-6 sm:p-8">
        <h2 className="mb-6 text-center text-[20px] font-black text-foreground sm:text-2xl">
          رویه‌های همیشه‌فعال
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => {
            const I = it.icon;
            return (
              <div key={it.title} className="rounded-2xl border border-border bg-card p-5">
                <I size={18} className="mb-2 text-primary-main" />
                <div className="text-[13.5px] font-black text-foreground">{it.title}</div>
                <div className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                  {it.desc}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-12 sm:px-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <div className="text-[28px] font-black text-primary-main">۹۹.۹۹٪</div>
          <div className="mt-1 text-[11.5px] text-muted-foreground">میانگین SLA پلتفرم</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <div className="text-[28px] font-black text-secondary-main">۰</div>
          <div className="mt-1 text-[11.5px] text-muted-foreground">رخنه داده در ۳۶ ماه گذشته</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <div className="text-[28px] font-black text-success">۹۰d</div>
          <div className="mt-1 text-[11.5px] text-muted-foreground">نگهداری audit log</div>
        </div>
      </div>
    </section>
  );
}

function SkelGrid() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-6">
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-3 rounded-2xl border border-border bg-card p-5">
            <ShimmerBar className="h-11 w-11 rounded-xl" />
            <ShimmerBar className="h-4 w-2/3" />
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-5/6" />
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <ShimmerBar key={i} className="h-12" />
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-6">
      <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-8 text-center sm:p-12">
        <Shield size={20} className="mx-auto mb-3 text-primary-main" />
        <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">سؤال امنیتی داری؟</h2>
        <p className="mx-auto mb-6 max-w-md text-[13px] text-muted-foreground">
          تیم امنیت ما آماده پاسخ به همه سؤالات فنی و انطباقی است.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13px] font-black text-white hover:bg-primary-400"
          >
            تماس با تیم امنیت <ArrowLeft size={14} />
          </Link>
          <Link
            href="/status"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-[13px] font-black text-foreground hover:bg-muted"
          >
            وضعیت سرویس
          </Link>
        </div>
      </div>
    </section>
  );
}
